import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { cx } from 'react-emotion';

import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';

import { formatAccountDetailsLine } from '../../utils/accountUtils';
import { paymentReviewContent } from './PaymentReview.content';
import { selectAuthorisorStyles, authorisationPanelStyles, paymentReviewStatusStyles } from './styles/styles';
import { ErrorValidationMessage } from '../ErrorValidationMessage/ErrorValidationMessage';
import PaymentReviewRow from './PaymentReviewRow';
import { StandardLabel } from 'web-ui-components/lib/typography/labels';
import { SmallestBody } from 'web-ui-components/lib/typography/body';

const { noAvailableAuthorisersErrorMessage } = paymentReviewContent;

const PaymentReview = ({
	fromAccountName,
	fromAccountSortCode,
	fromAccountNumber,
	fromAccountHolder,
	toAccountName,
	toAccountSortCode,
	toAccountNumber,
	amount,
	reference,
	paymentDate,
	paymentType,
	status,
	repeating,
	statusTitle,
	statusSubTitle,
	hideStatus,
	hasNoAvailableAuthorisers,
	hasGroupApprovalsEnabled = false,
	hasStatus,
	labels: { toLabel, fromLabel, amtLabel, refLabel, dateLabel, typeLabel },
	onSelectAuthorisers,
	requiresAuth,
	authorisers,
	content: { changeAuthoriserButton = '', selectAuthoriserButton = '', viewAuthoriserButton = '' } = {},
	isDefaultAuthorisers,
}) => {
	const classNames = classnames('c-m-payment-review', {
		'c-m-payment-review--has-status': hasStatus && status,
		'c-m-payment-review--status-warning': hasStatus && status === 'warning',
		'c-m-payment-review--status-success': hasStatus && status === 'success',
	});

	const getStatusClass = () => {
		switch (status) {
			case 'warning':
				return cx('c-m-payment-review__status', 'c-m-payment-review__status--warning', authorisationPanelStyles, {
					[selectAuthorisorStyles]: requiresAuth,
				});
			case 'success':
				return 'c-m-payment-review__status c-m-payment-review__status--success';
			case 'pending':
			case 'failed':
				return cx('c-m-payment-review__status', 'c-m-payment-review__status--failed', [paymentReviewStatusStyles]);
			default:
				return '';
		}
	};

	const getIconType = () => {
		switch (status) {
			case 'warning':
				return 'AuthorisationLarge';
			case 'success':
				return 'TickOutlineCircleLarge';
			case 'failed':
				return 'crossThinOutlineLarge';
			default:
				return '';
		}
	};

	const showAuthButton = requiresAuth && !isDefaultAuthorisers && !hasNoAvailableAuthorisers;
	const showHasNoAvailableAuthorisersError = requiresAuth && !isDefaultAuthorisers && hasNoAvailableAuthorisers;
	const selectAuthoriserButtonLabel =
		authorisers && authorisers.length ? changeAuthoriserButton : selectAuthoriserButton;

	return (
		<div className={classNames}>
			<PaymentReviewRow
				label={fromLabel}
				main={fromAccountName}
				sub={formatAccountDetailsLine(fromAccountSortCode, fromAccountNumber)}
				subTitle={fromAccountHolder}
				className="c-m-payment-review__row--double"
			/>
			<PaymentReviewRow
				label={toLabel}
				main={toAccountName}
				sub={formatAccountDetailsLine(toAccountSortCode, toAccountNumber)}
				className="c-m-payment-review__row--double"
				wrapMain
			/>

			<PaymentReviewRow label={amtLabel} main={`£${amount}`} />
			<PaymentReviewRow label={refLabel} main={reference} className="c-m-payment-review__row--ref" />
			<PaymentReviewRow label={dateLabel} main={paymentDate} />
			<PaymentReviewRow label={typeLabel} main={paymentType} />

			{!hideStatus && (
				<div className={getStatusClass()}>
					<div className="c-m-payment-review__status-label">
						<Icon iconType={getIconType()} size="sm" />
					</div>
					<div className="c-m-payment-review__status-value">
						<StandardLabel isFlush>{statusTitle}</StandardLabel>
						<SmallestBody isFlush>{statusSubTitle}</SmallestBody>
					</div>

					{showHasNoAvailableAuthorisersError && (
						<ErrorValidationMessage data-test-id="no-available-error-authorisers-error-message">
							{noAvailableAuthorisersErrorMessage}
						</ErrorValidationMessage>
					)}

					{showAuthButton && (
						<NavigationButton.Tertiary
							data-test-id="view-authorisers-button"
							isInline
							onClick={onSelectAuthorisers}
							labelText={hasGroupApprovalsEnabled ? viewAuthoriserButton : selectAuthoriserButtonLabel}
						/>
					)}
				</div>
			)}

			{!!repeating && repeating}
		</div>
	);
};

PaymentReview.defaultProps = {
	hasNoAvailableAuthorisers: false,
	hasStatus: false,
	requiresAuth: false,
};

PaymentReview.propTypes = {
	fromAccountName: PropTypes.string.isRequired,
	fromAccountSortCode: PropTypes.string.isRequired,
	fromAccountNumber: PropTypes.string.isRequired,
	fromAccountHolder: PropTypes.string,
	toAccountName: PropTypes.string.isRequired,
	toAccountSortCode: PropTypes.string.isRequired,
	toAccountNumber: PropTypes.string.isRequired,
	amount: PropTypes.string.isRequired,
	reference: PropTypes.string,
	paymentDate: PropTypes.string.isRequired,
	paymentType: PropTypes.string.isRequired,
	status: PropTypes.string,
	repeating: PropTypes.string,
	statusTitle: PropTypes.string,
	statusSubTitle: PropTypes.string,
	hideStatus: PropTypes.bool,
	hasNoAvailableAuthorisers: PropTypes.bool,
	hasGroupApprovalsEnabled: PropTypes.bool,
	hasStatus: PropTypes.bool,
	labels: PropTypes.shape({
		fromLabel: PropTypes.string,
		toLabel: PropTypes.string,
		amtLabel: PropTypes.string,
		refLabel: PropTypes.string,
		dateLabel: PropTypes.string,
		typeLabel: PropTypes.string,
	}),

	payment: PropTypes.shape({}),
	onSelectAuthorisers: PropTypes.func,
	requiresAuth: PropTypes.bool,
	content: PropTypes.shape({}),
	isDefaultAuthorisers: PropTypes.bool,
};

export { PaymentReview };
